/**
 * Recipes Table Component
 * @see sprint-datagrid-migration Phase 1.7
 */

import { useMemo, useCallback } from 'react';
import { ChefHat, MoreHorizontal, Copy, Archive, Calculator, FileEdit } from 'lucide-react';
import {
  DataGrid,
  Badge,
  EmptyState,
  Dropdown,
  DropdownItem,
  DropdownDivider,
  textColumn,
  integerColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import type { RecipeListItem, PaginationInfo } from '@bstock/shared';

export type SortField = 'name' | 'createdAt' | 'updatedAt' | 'version' | 'theoreticalCost';
export type SortOrder = 'asc' | 'desc';

const SORT_FIELD_MAP: Record<string, SortField> = {
  name: 'name',
  version: 'version',
  theoreticalCost: 'theoreticalCost',
};

interface RecipesTableProps {
  recipes: RecipeListItem[];
  pagination: PaginationInfo;
  isLoading: boolean;
  error: Error | null;
  onRowClick: (recipeId: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  sortBy: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField, order: SortOrder) => void;
  onDuplicate: (recipeId: string) => void;
  onArchive: (recipeId: string) => void;
  onCalculateCost: (recipeId: string) => void;
  hasFilters: boolean;
  onCreateClick: () => void;
  onFetchAllRows?: () => Promise<RecipeListItem[]>;
  showFinancials?: boolean;
}

export function RecipesTable({
  recipes,
  pagination,
  isLoading,
  error,
  onRowClick,
  onPageChange,
  onLimitChange,
  sortBy,
  sortOrder,
  onSort,
  onDuplicate,
  onArchive,
  onCalculateCost,
  hasFilters,
  onCreateClick,
  onFetchAllRows,
  showFinancials = false,
}: RecipesTableProps) {
  const currency = useCurrency();

  const columns = useMemo<DataGridColumn<RecipeListItem>[]>(() => {
    const cols: DataGridColumn<RecipeListItem>[] = [
      textColumn<RecipeListItem>({
        id: 'name',
        header: 'Nom',
        accessor: 'name',
        ellipsis: true,
        maxWidth: '300px',
        cell: (r) => (
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                r.isDraft ? 'bg-warning-50' : 'bg-primary-50'
              }`}
            >
              {r.isDraft ? (
                <FileEdit className="w-4 h-4 text-warning-500" />
              ) : (
                <ChefHat className="w-4 h-4 text-primary-500" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-dark-500 truncate">{r.name}</span>
                {r.hasDraft && !r.isDraft && (
                  <span
                    className="inline-block w-2 h-2 rounded-full bg-warning-500 flex-shrink-0"
                    title="Brouillon en cours"
                  />
                )}
              </div>
              <div className="text-xs text-neutral-400">{r.recipeCode}</div>
            </div>
          </div>
        ),
      }),
      textColumn<RecipeListItem>({
        id: 'posProducts',
        header: 'Produit(s) fini(s)',
        sortable: false,
        accessor: (r) =>
          r.posProducts && r.posProducts.length > 0
            ? r.posProducts.map((p) => p.name).join(', ')
            : 'Orpheline',
        cell: (r) =>
          r.posProducts && r.posProducts.length > 0 ? (
            <span className="text-dark-500">
              {r.posProducts.length === 1
                ? r.posProducts[0].name
                : `${r.posProducts[0].name} (+${r.posProducts.length - 1})`}
            </span>
          ) : (
            <Badge variant="neutral">Orpheline</Badge>
          ),
      }),
      integerColumn<RecipeListItem>({
        id: 'ingredientsCount',
        header: 'Ingrédients',
        sortable: false,
        align: 'center',
        accessor: 'ingredientsCount',
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<RecipeListItem>({
          id: 'theoreticalCost',
          header: 'Coût théorique',
          accessor: (r) =>
            r.theoreticalCost !== null ? r.theoreticalCost / 100 : null,
          cell: (r) =>
            r.theoreticalCost !== null ? (
              <span className="font-medium text-dark-500">
                {formatCentimes(r.theoreticalCost, currency)}
              </span>
            ) : (
              <Badge variant="warning">Non calculé</Badge>
            ),
        })
      );
    }

    cols.push(
      textColumn<RecipeListItem>({
        id: 'version',
        header: 'Version',
        align: 'center',
        accessor: (r) => (r.isDraft ? 'Brouillon' : `V${r.version}`),
        cell: (r) =>
          r.isDraft ? (
            <Badge variant="warning">Brouillon</Badge>
          ) : (
            <Badge variant="neutral">V{r.version}</Badge>
          ),
      }),
      textColumn<RecipeListItem>({
        id: 'actions',
        header: '',
        sortable: false,
        exportSkip: true,
        width: '50px',
        align: 'right',
        cell: (r) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={
                <button className="p-1 hover:bg-neutral-100 rounded-md transition-colors">
                  <MoreHorizontal className="w-5 h-5 text-neutral-500" />
                </button>
              }
              align="right"
            >
              {showFinancials && (
                <DropdownItem
                  icon={<Calculator className="w-4 h-4" />}
                  onClick={() => onCalculateCost(r.id)}
                >
                  Calculer le coût
                </DropdownItem>
              )}
              <DropdownItem
                icon={<Copy className="w-4 h-4" />}
                onClick={() => onDuplicate(r.id)}
              >
                Dupliquer
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem
                icon={<Archive className="w-4 h-4" />}
                onClick={() => onArchive(r.id)}
                className="text-danger-500 hover:bg-danger-50"
              >
                Archiver
              </DropdownItem>
            </Dropdown>
          </div>
        ),
      })
    );

    return cols;
  }, [currency, showFinancials, onCalculateCost, onDuplicate, onArchive]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, v]) => v === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('name', 'asc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  if (error) {
    return (
      <EmptyState
        icon={ChefHat}
        title="Erreur lors du chargement"
        description={error.message}
      />
    );
  }

  if (!isLoading && recipes.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={ChefHat}
        title="Aucune recette trouvée"
        description="Essayez de modifier vos filtres de recherche"
      />
    ) : (
      <EmptyState
        icon={ChefHat}
        title="Aucune recette"
        description="Commencez par créer votre première fiche technique"
        action={{ label: 'Créer une recette', onClick: onCreateClick, icon: ChefHat }}
      />
    );
  }

  return (
    <DataGrid<RecipeListItem>
      data={recipes}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={10}
      onRowClick={(r) => onRowClick(r.id)}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'recettes' },
        sheetName: 'Recettes',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune recette trouvée
        </div>
      }
    />
  );
}
